import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { modals } from "@mantine/modals";
import {
  IconBook2,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCopy,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import "dayjs/locale/vi";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { useHomeBlockStore } from "../../store/useHomeBlockStore";
import AddBlockModal from "./AddBlockModal";
import { GUIDE_PATH } from "./HomeBlockGuidePage";
import {
  DragHandle,
  formatSchedule,
  SortableArea,
  useSortableItem,
  ZoomableImage,
} from "./HomeBlockShared";
import { ACCENT, designImage, HomeBlock, templateInfo } from "./homeBlockData";
import classes from "./homeBlock.module.css";

type Filter = "all" | "on" | "off" | "sched";
type Device = "desktop" | "mobile";

const editPath = (block: HomeBlock) => `/fixed-content/home-block/${block.id}`;

type BlockRowProps = {
  block: HomeBlock;
  order: number;
  isFirst: boolean;
  isLast: boolean;
  selected: boolean;
  device: Device;
  onPick: () => void;
  onEdit: () => void;
};

const BlockRow = ({
  block,
  order,
  isFirst,
  isLast,
  selected,
  device,
  onPick,
  onEdit,
}: BlockRowProps) => {
  const { setNodeRef, style, isDragging, handleProps } = useSortableItem(
    block.id
  );
  const autoSave = useHomeBlockStore((s) => s.autoSave);
  const move = useHomeBlockStore((s) => s.move);
  const unsaved = useHomeBlockStore((s) => s.dirtyIds.includes(block.id));

  const offDevice = device === "mobile" ? !block.mobile : !block.desktop;
  const schedule = formatSchedule(block.from, block.to);
  const rowClass = [
    classes.row,
    selected ? classes.rowSelected : "",
    !block.visible || offDevice ? classes.rowOff : "",
    isDragging ? classes.rowDragging : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Bấm vào chỗ trống trên dòng cũng chọn block; nút, công tắc, ô nhập giữ hành vi riêng
  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button, input, a, label")) return;
    onPick();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${rowClass} ${classes.rowClickable}`}
      onClick={handleRowClick}
    >
      <DragHandle
        label={`Kéo để đổi thứ tự ${block.name}`}
        handleProps={handleProps}
      />
      <span className={classes.order}>{String(order).padStart(2, "0")}</span>
      <img
        className={classes.thumb}
        src={designImage(block.thumbId, 480)}
        alt={`Ảnh thiết kế ${block.name}`}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
      <Stack gap={5} miw={0}>
        <button
          type="button"
          className={classes.rowName}
          aria-pressed={selected}
          onClick={onPick}
        >
          {block.name}
        </button>
        <Group gap={6}>
          {block.templateMissing ? (
            <Badge variant="light" color="red" radius="xl" tt="none" fw={600}>
              Chưa rõ mẫu
            </Badge>
          ) : (
            <Badge variant="light" color="gray" radius="xl" tt="none" fw={600}>
              {templateInfo(block.template).name}
            </Badge>
          )}
          {unsaved && (
            <Badge variant="light" color="orange" radius="xl" tt="none" fw={600}>
              Chưa lưu
            </Badge>
          )}
          {!block.visible && (
            <Badge variant="outline" color="gray" radius="xl" tt="none" fw={600}>
              Đang ẩn
            </Badge>
          )}
          {block.visible && offDevice && (
            <Badge variant="light" color="orange" radius="xl" tt="none" fw={600}>
              {device === "mobile"
                ? "Không hiện trên điện thoại"
                : "Không hiện trên máy tính"}
            </Badge>
          )}
        </Group>
      </Stack>
      <Group gap={8} wrap="nowrap">
        <span
          className={block.desktop ? classes.devOn : classes.devOff}
          title={block.desktop ? "Hiện trên máy tính" : "Ẩn trên máy tính"}
        >
          <IconDeviceDesktop size={18} aria-hidden />
        </span>
        <span
          className={block.mobile ? classes.devOn : classes.devOff}
          title={block.mobile ? "Hiện trên điện thoại" : "Ẩn trên điện thoại"}
        >
          <IconDeviceMobile size={18} aria-hidden />
        </span>
      </Group>
      <Box miw={0}>
        {schedule ? (
          <Badge
            variant="light"
            color="orange"
            radius="xl"
            tt="none"
            fw={600}
            leftSection={<IconClock size={14} />}
          >
            {schedule}
          </Badge>
        ) : (
          <Text fz={13} className={classes.muted}>
            Luôn hiển thị
          </Text>
        )}
      </Box>
      <Switch
        color={ACCENT}
        aria-label={`Bật block ${block.name}`}
        checked={block.visible}
        onChange={(e) =>
          autoSave(block.id, { visible: e.currentTarget.checked })
        }
      />
      <Group gap={2} wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={`Chuyển ${block.name} lên trên`}
          disabled={isFirst}
          onClick={() => move(block.id, -1)}
        >
          <IconChevronUp size={16} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={`Chuyển ${block.name} xuống dưới`}
          disabled={isLast}
          onClick={() => move(block.id, 1)}
        >
          <IconChevronDown size={16} />
        </ActionIcon>
        <ActionIcon
          variant="light"
          color={ACCENT}
          aria-label={`Sửa nội dung ${block.name}`}
          title="Sửa nội dung"
          onClick={onEdit}
        >
          <IconPencil size={16} />
        </ActionIcon>
      </Group>
    </div>
  );
};

type DetailPanelProps = {
  block: HomeBlock;
  order: number;
};

const DetailPanel = ({ block, order }: DetailPanelProps) => {
  const navigate = useNavigate();
  const autoSave = useHomeBlockStore((s) => s.autoSave);
  const duplicate = useHomeBlockStore((s) => s.duplicate);
  const remove = useHomeBlockStore((s) => s.remove);
  const saving = useHomeBlockStore((s) => s.saving);
  const info = templateInfo(block.template);

  const confirmRemove = () =>
    modals.openConfirmModal({
      title: "Xoá block",
      children: (
        <Text fz={14}>
          Xoá block "{block.name}" khỏi trang chủ? Block bị xoá luôn trên hệ
          thống, không hoàn tác được.
        </Text>
      ),
      labels: { confirm: "Xoá block", cancel: "Huỷ" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        if (await remove(block.id)) {
          NotificationExtension.Success(`Đã xoá block ${block.name}`);
        }
      },
    });

  return (
    <Paper
      withBorder
      radius="md"
      component="aside"
      aria-label="Chi tiết block đang chọn"
      style={{ overflow: "hidden" }}
    >
      <Stack gap={10} px={16} pt={16}>
        <Group justify="space-between" gap={8}>
          <Text fz={12} fw={600} className={classes.muted}>
            Block {String(order).padStart(2, "0")} trên trang
          </Text>
          <Badge variant="light" color="gray" radius="xl" tt="none" fw={600}>
            {info.name}
          </Badge>
        </Group>
        <Title order={2} fz={18} lh={1.3}>
          {block.name}
        </Title>
        <Text fz={13} className={classes.muted}>
          Mã block:{" "}
          <Text component="code" fz={13} c="var(--hb-ink-2)" className={classes.mono}>
            {block.code}
          </Text>
        </Text>
      </Stack>
      <Box component="figure" mx={16} mt={14} mb={0}>
        <ZoomableImage imageId={block.thumbId} alt={`Ảnh thiết kế ${block.name}`} />
        <Text component="figcaption" fz={12} mt={6} className={classes.muted}>
          Ảnh thiết kế của block, bấm để phóng to.
        </Text>
      </Box>
      <Stack gap={14} p={16}>
        <TextInput
          label="Tiêu đề hiển thị"
          value={block.name}
          readOnly
          description="Đổi tiêu đề trong trình sửa nội dung."
          inputWrapperOrder={["label", "input", "description"]}
        />
        <Checkbox.Group
          label="Hiển thị trên"
          value={[
            ...(block.desktop ? ["desktop"] : []),
            ...(block.mobile ? ["mobile"] : []),
          ]}
          onChange={(value) =>
            autoSave(block.id, {
              desktop: value.includes("desktop"),
              mobile: value.includes("mobile"),
            })
          }
        >
          <Group mt={6} gap="lg">
            <Checkbox color={ACCENT} value="desktop" label="Máy tính" />
            <Checkbox color={ACCENT} value="mobile" label="Điện thoại" />
          </Group>
        </Checkbox.Group>
        <SimpleGrid cols={2} spacing={10}>
          <DateTimePicker
            label="Hiện từ"
            placeholder="dd/mm/yyyy hh:mm"
            valueFormat="DD/MM/YYYY HH:mm"
            locale="vi"
            clearable
            value={block.from}
            onChange={(value) => autoSave(block.id, { from: value })}
          />
          <DateTimePicker
            label="Đến"
            placeholder="Không giới hạn"
            valueFormat="DD/MM/YYYY HH:mm"
            locale="vi"
            clearable
            value={block.to}
            minDate={block.from ?? undefined}
            onChange={(value) => autoSave(block.id, { to: value })}
          />
        </SimpleGrid>
        <Text fz={12} className={classes.muted} mt={-6}>
          Để trống nếu block luôn hiển thị. Hết lịch, block tự ẩn.
        </Text>
        <Button
          fullWidth
          variant="outline"
          color={ACCENT}
          leftSection={<IconPencil size={16} />}
          onClick={() => navigate(editPath(block))}
        >
          Sửa nội dung block
        </Button>
        <Group gap={8} grow>
          <Button
            variant="default"
            leftSection={<IconCopy size={16} />}
            loading={saving}
            onClick={async () => {
              if (await duplicate(block.id)) {
                NotificationExtension.Success("Đã nhân bản block, bản sao đang ẩn");
              }
            }}
          >
            Nhân bản
          </Button>
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={confirmRemove}
          >
            Xoá block
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

const HomeBlockList = () => {
  const navigate = useNavigate();
  const blocks = useHomeBlockStore((s) => s.blocks);
  const selectedId = useHomeBlockStore((s) => s.selectedId);
  const select = useHomeBlockStore((s) => s.select);
  const status = useHomeBlockStore((s) => s.status);
  const load = useHomeBlockStore((s) => s.load);
  const reorder = useHomeBlockStore((s) => s.reorder);

  useEffect(() => {
    load();
  }, [load]);

  const [filter, setFilter] = useState<Filter>("all");
  const [device, setDevice] = useState<Device>("desktop");
  const [query, setQuery] = useState("");
  const [addOpened, setAddOpened] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  // Màn hẹp: bảng chi tiết rơi xuống dưới danh sách, chọn xong thì cuộn tới để thấy ngay
  const pickBlock = (id: string) => {
    select(id);
    const list = listRef.current;
    const detail = detailRef.current;
    if (
      list &&
      detail &&
      detail.getBoundingClientRect().top >= list.getBoundingClientRect().bottom
    ) {
      detail.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const counts = useMemo(
    () => ({
      all: blocks.length,
      on: blocks.filter((b) => b.visible).length,
      off: blocks.filter((b) => !b.visible).length,
      sched: blocks.filter((b) => b.from || b.to).length,
    }),
    [blocks]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blocks.filter((b) => {
      if (filter === "on" && !b.visible) return false;
      if (filter === "off" && b.visible) return false;
      if (filter === "sched" && !(b.from || b.to)) return false;
      if (
        q &&
        !b.name.toLowerCase().includes(q) &&
        !b.code.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [blocks, filter, query]);

  const selectedIndex = blocks.findIndex((b) => b.id === selectedId);
  const selected = selectedIndex >= 0 ? blocks[selectedIndex] : blocks[0];
  const selectedOrder = selectedIndex >= 0 ? selectedIndex + 1 : 1;

  const handleReorder = (from: number, to: number) => {
    const fromIndex = blocks.findIndex((b) => b.id === rows[from].id);
    const toIndex = blocks.findIndex((b) => b.id === rows[to].id);
    reorder(fromIndex, toIndex);
  };

  const segLabel = (text: string, count: number) => (
    <Group gap={6} wrap="nowrap" justify="center">
      <span>{text}</span>
      <Text component="span" fz={13} fw={600} className={classes.muted}>
        {count}
      </Text>
    </Group>
  );

  return (
    <Box className={classes.page}>
      <BreadCrumb />
      <Stack gap={16} mt={8}>
        <Paper withBorder radius="md" p={12}>
          <Flex wrap="wrap" align="center" gap={12}>
            <Box maw="100%" style={{ overflowX: "auto" }}>
              <SegmentedControl
                aria-label="Lọc block"
                value={filter}
                onChange={(value) => setFilter(value as Filter)}
                data={[
                  { value: "all", label: segLabel("Tất cả", counts.all) },
                  { value: "on", label: segLabel("Đang hiển thị", counts.on) },
                  { value: "off", label: segLabel("Đang ẩn", counts.off) },
                  { value: "sched", label: segLabel("Có lịch", counts.sched) },
                ]}
              />
            </Box>
            <TextInput
              aria-label="Tìm block theo tên hoặc mã"
              placeholder="Tìm theo tên hoặc mã block"
              leftSection={<IconSearch size={16} />}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              style={{ flex: "1 1 220px", maxWidth: 320 }}
            />
            <Box style={{ flex: "1 1 auto" }} />
            <Button
              variant="default"
              leftSection={<IconBook2 size={16} />}
              onClick={() => navigate(GUIDE_PATH)}
            >
              Hướng dẫn sử dụng
            </Button>
            <SegmentedControl
              aria-label="Xem theo thiết bị"
              value={device}
              onChange={(value) => setDevice(value as Device)}
              data={[
                {
                  value: "desktop",
                  label: (
                    <Group gap={6} wrap="nowrap" justify="center">
                      <IconDeviceDesktop size={16} />
                      <span>Máy tính</span>
                    </Group>
                  ),
                },
                {
                  value: "mobile",
                  label: (
                    <Group gap={6} wrap="nowrap" justify="center">
                      <IconDeviceMobile size={16} />
                      <span>Điện thoại</span>
                    </Group>
                  ),
                },
              ]}
            />
            <Button
              variant="outline"
              color={ACCENT}
              leftSection={<IconPlus size={16} />}
              onClick={() => setAddOpened(true)}
            >
              Thêm block
            </Button>
          </Flex>
        </Paper>

        {status === "error" && (
          <Alert color="red" variant="light" radius="md" title="Không tải được danh sách block">
            <Group gap={10}>
              <Text fz={14}>Kiểm tra kết nối hoặc đăng nhập lại rồi thử lại.</Text>
              <Button size="xs" variant="default" onClick={() => load(true)}>
                Tải lại
              </Button>
            </Group>
          </Alert>
        )}

        <Flex wrap="wrap" align="flex-start" gap={16}>
          <Paper
            ref={listRef}
            withBorder
            radius="md"
            component="section"
            aria-label="Danh sách block"
            style={{ flex: "999 1 640px", minWidth: 0, overflow: "hidden" }}
          >
            <Box style={{ overflowX: "auto" }}>
              <Box miw={860}>
                <div className={`${classes.row} ${classes.rowHeader}`}>
                  <span />
                  <span>STT</span>
                  <span>Ảnh thiết kế</span>
                  <span>Block</span>
                  <span>Thiết bị</span>
                  <span>Lịch hiển thị</span>
                  <span>Bật</span>
                  <span>Thao tác</span>
                </div>
                <SortableArea
                  ids={rows.map((b) => b.id)}
                  onReorder={handleReorder}
                >
                  {rows.map((block) => {
                    const index = blocks.findIndex((b) => b.id === block.id);
                    return (
                      <BlockRow
                        key={block.id}
                        block={block}
                        order={index + 1}
                        isFirst={index === 0}
                        isLast={index === blocks.length - 1}
                        selected={block.id === selected?.id}
                        device={device}
                        onPick={() => pickBlock(block.id)}
                        onEdit={() => navigate(editPath(block))}
                      />
                    );
                  })}
                </SortableArea>
                {(status === "idle" || status === "loading") && blocks.length === 0 && (
                  <Group
                    justify="center"
                    gap={10}
                    py={48}
                    style={{ borderTop: "1px solid var(--hb-border)" }}
                  >
                    <Loader size="sm" color={ACCENT} />
                    <Text fz={14} className={classes.muted}>
                      Đang tải danh sách block...
                    </Text>
                  </Group>
                )}
                {status === "ready" && blocks.length === 0 && (
                  <Stack
                    align="center"
                    gap={10}
                    py={48}
                    px={16}
                    style={{
                      borderTop: "1px solid var(--hb-border)",
                      textAlign: "center",
                    }}
                  >
                    <Text fz={15} fw={600}>
                      Trang chủ chưa có block nào
                    </Text>
                    <Text fz={14} className={classes.muted}>
                      Bấm Thêm block để chọn mẫu và tạo block đầu tiên.
                    </Text>
                    <Button
                      variant="outline"
                      color={ACCENT}
                      leftSection={<IconPlus size={16} />}
                      onClick={() => setAddOpened(true)}
                    >
                      Thêm block
                    </Button>
                  </Stack>
                )}
                {blocks.length > 0 && rows.length === 0 && (
                  <Stack
                    align="center"
                    gap={10}
                    py={48}
                    px={16}
                    style={{
                      borderTop: "1px solid var(--hb-border)",
                      textAlign: "center",
                    }}
                  >
                    <Text fz={15} fw={600}>
                      Không có block nào khớp bộ lọc
                    </Text>
                    <Text fz={14} className={classes.muted}>
                      Thử bỏ từ khoá tìm kiếm hoặc chọn lại bộ lọc Tất cả.
                    </Text>
                    <Button
                      variant="default"
                      onClick={() => {
                        setFilter("all");
                        setQuery("");
                      }}
                    >
                      Xoá bộ lọc
                    </Button>
                  </Stack>
                )}
              </Box>
            </Box>
          </Paper>

          {selected && (
            <Box
              ref={detailRef}
              className={classes.detailSticky}
              style={{ flex: "1 1 340px", maxWidth: "100%", minWidth: 0 }}
            >
              <DetailPanel block={selected} order={selectedOrder} />
            </Box>
          )}
        </Flex>
      </Stack>

      <AddBlockModal opened={addOpened} onClose={() => setAddOpened(false)} />
    </Box>
  );
};

export default HomeBlockList;
